import styles from 'css/src/components/common/video.module.scss';

import React from 'react';

type Props = {
    preview_url?: string;
    poster?: string;
    min_playback_duration?: number; // stretch playback to this duration, seconds
    className?: string;
    loop?: boolean;
    muted?: boolean;
    controls?: boolean;
    hideMuteControl?: boolean;
    playing?: boolean;
    play_on_hover?: boolean;
    reset_on_stop?: boolean; // jumps back to the start of the video when stopped
    onClick?: VoidFunction;
    onTouchStart?: VoidFunction;
    onLoading?: VoidFunction;
    onPlay?: VoidFunction;
    onStop?: VoidFunction;
    allowFullscreen?: boolean;
};

// Helper function to unmount video (moved outside component)
const unmountVideo = (videoRef: React.RefObject<HTMLVideoElement | null>) => {
    if (videoRef.current) {
        // clear the src attributes
        const source_children = videoRef.current.querySelectorAll('source');
        for (let i = 0; i < source_children.length; i++) {
            source_children[i].removeAttribute('src');
        }

        // call `.load()` to force any old request to be terminated (since the source is now cleared)
        try {
            videoRef.current.load();
        } catch (e) {
            // Firefox errors with `AbortError` when this causes requests to be cancelled so wrap
            // in try/catch to prevent emitting error (see https://lumen5.atlassian.net/browse/MR-2359)
        }
    }
};

const Video = React.memo(function Video({
    preview_url,
    poster = '',
    min_playback_duration,
    className = '',
    loop = false,
    muted = false,
    controls = false,
    hideMuteControl = false,
    playing,
    play_on_hover,
    reset_on_stop = true,
    onClick,
    onTouchStart,
    onLoading,
    onPlay,
    onStop,
    allowFullscreen = false,
}: Props) {
    /*
    Use this Video component to simply and easily create well-behaving HTML5 <video>s.

    Some handling:
    - <video> tags will NOT refresh to show an update if the source url(s) update, so we force
      react to redraw by adding a key set to the preview_url
    - set the min_playback_duration (seconds) to stretch the duration if the video should be played
      slower.
    - set play_on_hover to true to easily control playback.
    - Chrome breaks when mp4's don't completely load and swamp the request queue (ie. when a bunch
      are playing).  This component force unloads the <Video> to ensure that requests to ensure
      that this doesn't happen.

    You can control when a video is playing via two methods: on-hover or via a property.  Simply
    set `play_on_hover=True` and the component will handle playing the video when the user is
    hovering over the component.  Alternatively, you can set the `playing` property directly
    to control whether the video is playing or not.
    */
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const hoveringTimeoutRef = React.useRef<number | null>(null);
    const handleLoadedMetadataRef = React.useRef<(() => void) | null>(null);
    const prevPlayingRef = React.useRef<boolean | undefined>(playing);
    const isPlayingRef = React.useRef(playing ?? false);

    const updatePlaybackRate = React.useCallback(() => {
        // adjust playbackRate if we want to stretch the video duration
        if (!videoRef.current) {
            return;
        }

        if (isNaN(videoRef.current.duration)) {
            return;
        }

        if (min_playback_duration === undefined) {
            return;
        }

        if (videoRef.current.duration < min_playback_duration) {
            let new_playback_rate = videoRef.current.duration / min_playback_duration;
            if (new_playback_rate < 0.0625) {
                // most browsers including Chrome & Firefox only support a minimum rate of 0.0625
                new_playback_rate = 0.0625;
            }
            videoRef.current.playbackRate = new_playback_rate;
        } else {
            videoRef.current.playbackRate = 1.0;
        }
    }, [min_playback_duration]);

    const stop = React.useCallback(() => {
        if (videoRef.current) {
            if (videoRef.current.paused === false && videoRef.current.ended === false) {
                videoRef.current.pause();
            }

            // check if reset to beginning of playback desired
            if (reset_on_stop) {
                // only set currentTime to 0 if the metadata has loaded, otherwise IE error
                if (!isNaN(videoRef.current.duration)) {
                    videoRef.current.currentTime = 0;
                }
            }
        }

        isPlayingRef.current = false;

        // Chrome hangs when too many mp4 videos are loading, so force unload
        // the video element when stopping to terminate any pending requests.
        unmountVideo(videoRef);

        if (onStop) {
            onStop();
        }
    }, [reset_on_stop, onStop]);

    const checkIfHoveringToContinuePlayback = React.useCallback(() => {
        // If the play_on_hover property is set, we will automatically play the video
        // when the mouseEnter event is called.  One of the challenges is if you move your
        // mouse very quickly, you can move your mouse out of the element before the mouseLeave
        // event has a chance to fire, so the video will continue to play even if we don't want it
        // to. This method sets up a callback which fires on a schedule while playing to check that
        // if we're only playing due to hover, that the mouse is actually hovering!  If we detect
        // that we're no longer hovering, this method will stop the video playback.
        if (isPlayingRef.current === false || !play_on_hover) {
            return;
        }

        if (!videoRef.current) {
            // try again later
            hoveringTimeoutRef.current = window.setTimeout(checkIfHoveringToContinuePlayback, 250);
            return;
        }

        const hovering_element = videoRef.current?.parentElement?.querySelector(':hover');
        if (hovering_element === videoRef.current) {
            // still hovering, yay!
            hoveringTimeoutRef.current = window.setTimeout(checkIfHoveringToContinuePlayback, 250);
        } else {
            // not hovering – stop the video!
            hoveringTimeoutRef.current = null;
            stop();
        }
    }, [play_on_hover, stop]);

    const handleLoadedMetadata = React.useCallback(() => {
        updatePlaybackRate();

        isPlayingRef.current = true;

        const playPromise = videoRef.current?.play();
        playPromise?.catch(() => {
            // for situations like hover-to-play, the promise could be rejected, which sends to the error reporter
            // We should just catch the error and ignore it.
        });

        if (onPlay) {
            onPlay();
        }
    }, [updatePlaybackRate, onPlay]);

    const play = React.useCallback(() => {
        if (!videoRef.current) {
            return;
        }

        // Clean up any existing event listener
        if (handleLoadedMetadataRef.current) {
            videoRef.current.removeEventListener('loadedmetadata', handleLoadedMetadataRef.current);
        }

        // Set the source directly before loading
        const sourceElement = videoRef.current.querySelector('source');
        if (sourceElement && preview_url) {
            sourceElement.setAttribute('src', preview_url);
        }

        videoRef.current.load();

        if (onLoading) {
            onLoading();
        }

        // One could argue we should wait for "canplay" instead here, but
        // since most MP4s store metadata at the beginning of the file,
        // this allows us to stream the file a bit more efficiently
        // (canplay, depending on browser implementation, will usually wait
        // until "the browser will be able to play to the end of the
        // content without buffering")
        //
        // Source: https://stackoverflow.com/a/28490876
        handleLoadedMetadataRef.current = handleLoadedMetadata;
        videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    }, [onLoading, handleLoadedMetadata, preview_url]);

    const startedPlaying = React.useCallback(() => {
        // this is called when the video changes state and starts playing - we want to make sure
        // the video still should be playing!
        if (isPlayingRef.current === false) {
            stop();
        } else {
            checkIfHoveringToContinuePlayback();
        }
    }, [stop, checkIfHoveringToContinuePlayback]);

    const handleMouseEnter = React.useCallback(() => {
        if (play_on_hover) {
            play();
        }
    }, [play_on_hover, play]);

    const handleMouseLeave = React.useCallback(() => {
        if (play_on_hover) {
            stop();
        }
    }, [play_on_hover, stop]);

    // Handle playing prop changes
    React.useEffect(() => {
        const prevPlaying = prevPlayingRef.current;
        prevPlayingRef.current = playing;

        if (playing === true) {
            play();
        } else if (playing === false) {
            stop();
        } else if (playing === undefined && prevPlaying !== undefined) {
            // Transition from defined to undefined - stop for safety
            stop();
        }
        // When playing starts as undefined and stays undefined, no action needed
    }, [playing, play, stop]);

    // Handle min_playback_duration changes
    React.useEffect(() => {
        updatePlaybackRate();
    }, [min_playback_duration, updatePlaybackRate]);

    // Cleanup on unmount
    React.useEffect(() => {
        const currentVideo = videoRef.current;

        return () => {
            unmountVideo(videoRef);

            if (hoveringTimeoutRef.current !== null) {
                window.clearTimeout(hoveringTimeoutRef.current);
            }

            // Clean up event listener
            if (handleLoadedMetadataRef.current && currentVideo) {
                currentVideo.removeEventListener('loadedmetadata', handleLoadedMetadataRef.current);
            }
        };
    }, []);

    const uniqueSourceKey = preview_url;

    // only set hover handlers if play_on_hover to avoid unused event handlers
    const mouseEnterHandler = play_on_hover ? handleMouseEnter : undefined;
    const mouseLeaveHandler = play_on_hover ? handleMouseLeave : undefined;

    let classes = `${styles['video-component']} ${className}`;

    if (hideMuteControl === true) {
        classes += ` ${styles['hide-mute']}`;
    }

    // determine the controls available (Chrome only)
    let controlsList = 'nodownload'; // never allow downloading
    if (!allowFullscreen) {
        controlsList += ' nofullscreen';
    }

    return (
        <video
            key={uniqueSourceKey}
            ref={videoRef}
            poster={poster}
            loop={loop}
            muted={muted}
            controls={controls}
            className={classes}
            onTouchStart={onTouchStart}
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            onDurationChange={updatePlaybackRate}
            preload="none"
            onPlay={startedPlaying}
            onPlaying={startedPlaying}
            onClick={onClick}
            draggable="false"
            disablePictureInPicture /* disable picture in picture in Chrome */
            playsInline={true} /* disable fullscreen in mobile browsers */
            disableRemotePlayback /* disable casting in Chrome */
            controlsList={controlsList}
        >
            <source />
        </video>
    );
});

export default Video;
